import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { PageHero } from '@/components/shared/PageHero';
import { storage, generateReference } from '@/lib/storage';
import { BARANGAY } from '@/data/mockData';
import { Send, Mail, Phone, MapPin, Clock, Facebook, Check } from 'lucide-react';
import { toast } from 'sonner';

const TOPICS = [
  'General Inquiry',
  'Document Request',
  'Schedule a Meeting',
  'Suggestion / Feedback',
  'Partnership / Sponsorship',
  'Media / Press',
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', topic: '', message: '', consent: false });
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.topic || !form.message) {
      return toast.error('Please complete all fields.');
    }
    if (!form.consent) {
      return toast.error('Please agree to the data privacy notice.');
    }
    const ref = generateReference('MSG');
    storage.push('messages', {
      id: ref,
      ...form,
      date: new Date().toISOString(),
    });
    toast.success('Message sent!', { description: `We will reply to ${form.email}.` });
    setSent(true);
    setForm({ name: '', email: '', topic: '', message: '', consent: false });
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Get in Touch"
        subtitle="Send us a message, ask a question, or schedule a visit. We typically reply within one working day."
        breadcrumbs={[{ label: 'Contact Us' }]}
      />

      <section className="container-app section-padding">
        <div className="grid lg:grid-cols-5 gap-8">
          <Card className="lg:col-span-3 p-6 sm:p-8 border-0 shadow-md-soft" data-testid="contact-form-card">
            <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2"><Send className="h-5 w-5 text-primary" /> Send a message</h2>
            <p className="text-sm text-muted-foreground mt-1">For document requests, please use the <strong>Request a Document</strong> form.</p>

            {sent ? (
              <div className="mt-6 p-6 rounded-xl bg-success/10 border border-success/30 text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-success/20 flex items-center justify-center">
                  <Check className="h-6 w-6 text-success" />
                </div>
                <h3 className="mt-3 font-heading font-semibold">Message received!</h3>
                <p className="mt-1 text-sm text-muted-foreground">Thank you. We will get back to you shortly.</p>
                <Button onClick={() => setSent(false)} variant="outline" className="mt-4" data-testid="contact-send-another">
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Full Name *</Label><Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Juan Dela Cruz" data-testid="contact-name" /></div>
                  <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" data-testid="contact-email" /></div>
                </div>
                <div>
                  <Label>Topic *</Label>
                  <Select value={form.topic} onValueChange={(v) => update('topic', v)}>
                    <SelectTrigger data-testid="contact-topic"><SelectValue placeholder="Select topic" /></SelectTrigger>
                    <SelectContent>
                      {TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Message *</Label>
                  <Textarea value={form.message} onChange={(e) => update('message', e.target.value)} rows={6} placeholder="How can we help?" data-testid="contact-message" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.consent}
                    onCheckedChange={(v) => update('consent', Boolean(v))}
                    data-testid="contact-consent"
                    className="mt-0.5"
                  />
                  <span className="text-sm text-muted-foreground">
                    I agree that my information will be used only to respond to this inquiry, in accordance with
                    the Data Privacy Act of 2012.
                  </span>
                </label>
                <Button type="submit" size="lg" className="w-full bg-gradient-primary text-primary-foreground" data-testid="contact-submit">
                  <Send className="mr-2 h-4 w-4" /> Send Message
                </Button>
              </form>
            )}
          </Card>

          <aside className="lg:col-span-2 space-y-4">
            <Card className="p-6 border-0 shadow-md-soft">
              <h3 className="font-heading font-semibold">Reach us directly</h3>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex gap-3"><MapPin className="h-4 w-4 mt-0.5 text-primary" /><span>{BARANGAY.address}</span></li>
                <li className="flex gap-3"><Phone className="h-4 w-4 mt-0.5 text-primary" /><a href={`tel:${BARANGAY.hotline}`} className="hover:underline">{BARANGAY.hotline}</a></li>
                <li className="flex gap-3"><Mail className="h-4 w-4 mt-0.5 text-primary" /><a href={`mailto:${BARANGAY.email}`} className="hover:underline break-all">{BARANGAY.email}</a></li>
                <li className="flex gap-3"><Clock className="h-4 w-4 mt-0.5 text-primary" /><div><div>{BARANGAY.officeHours}</div><div className="text-muted-foreground">{BARANGAY.saturdayHours}</div></div></li>
                <li className="flex gap-3"><Facebook className="h-4 w-4 mt-0.5 text-primary" /><a href={BARANGAY.facebook} target="_blank" rel="noreferrer" className="hover:underline">Facebook Page</a></li>
              </ul>
            </Card>
            <Card className="p-6 border-0 bg-gradient-to-br from-primary/5 to-accent-soft">
              <h3 className="font-heading font-semibold">Office Hours</h3>
              <div className="mt-3 text-sm/relaxed text-muted-foreground">
                <div><strong className="text-foreground">{BARANGAY.officeHours}</strong></div>
                <div><strong className="text-foreground">{BARANGAY.saturdayHours}</strong></div>
                <div className="mt-2 text-xs">Closed on Sundays and legal holidays. The barangay hotline operates 24/7 for emergencies.</div>
              </div>
            </Card>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
